import { loadSave, serializeSave, type LoadResult } from './migrate';
import type { Save } from './schema';

/** Minimal key-value storage; implemented by the platform layer (localStorage, Capacitor). */
export interface KeyValueStorage {
  get(key: string): string | null;
  set(key: string, value: string): void;
}

export const SAVE_KEY = 'lm.save';
export const BACKUP_KEY = 'lm.save.backup';

/**
 * Owns the player's save: loads (migrating or recovering), hands out the current document, and
 * persists after each change. Writes happen at safe points (results, purchases, settings).
 */
export class SaveStore {
  private current: Save;
  readonly loadResult: LoadResult;

  constructor(private readonly storage: KeyValueStorage) {
    const raw = storage.get(SAVE_KEY);
    this.loadResult = loadSave(raw);
    this.current = this.loadResult.save;
    if (this.loadResult.status === 'corrupt' && raw !== null) {
      // Keep the unreadable save so nothing is lost for good; start fresh.
      storage.set(BACKUP_KEY, raw);
    }
    if (this.loadResult.status !== 'loaded' || this.loadResult.migratedFrom !== undefined) {
      this.persist();
    }
  }

  get save(): Readonly<Save> {
    return this.current;
  }

  /** Applies a change to a copy of the save, then persists it. */
  update(change: (draft: Save) => void): Readonly<Save> {
    const draft = structuredClone(this.current);
    change(draft);
    this.current = draft;
    this.persist();
    return this.current;
  }

  /** Replaces the whole save (progress moved in from a save code). */
  replace(save: Save): Readonly<Save> {
    this.current = structuredClone(save);
    this.persist();
    return this.current;
  }

  private persist(): void {
    this.storage.set(SAVE_KEY, serializeSave(this.current));
  }
}

/** In-memory storage for tests and as a fallback when real storage is unavailable. */
export class MemoryStorage implements KeyValueStorage {
  private readonly map = new Map<string, string>();
  get(key: string): string | null {
    return this.map.get(key) ?? null;
  }
  set(key: string, value: string): void {
    this.map.set(key, value);
  }
}
